import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';

export interface ChatMessageProps {
  /**
   * `user` — the person's own message: a light bubble on the right.
   * `assistant` — a reply: full width, so tables and charts have room.
   */
  role: 'user' | 'assistant';
  children: React.ReactNode;
  /** Shown above a user bubble, such as the files sent with it. */
  above?: React.ReactNode;
}

/** A line that starts with a quote bar — ">" or the block characters some apps paste. */
const QUOTE_LINE = /^\s*[>▎▍▌▏│┃]\s?/;
/** A long message folds to about this many lines until opened. */
const FOLDED_LINES = 6;

/** Your own text, as typed, with pasted quote bars shown as a quiet quote. */
function UserText({ text }: { text: string }) {
  const groups: Array<{ quote: boolean; lines: string[] }> = [];
  for (const line of text.split('\n')) {
    const quote = QUOTE_LINE.test(line);
    const last = groups[groups.length - 1];
    const clean = quote ? line.replace(QUOTE_LINE, '') : line;
    if (last && last.quote === quote) last.lines.push(clean);
    else groups.push({ quote, lines: [clean] });
  }
  return (
    <>
      {groups.map((g, i) =>
        g.quote ? (
          <Box
            key={i}
            component="blockquote"
            sx={(t) => ({
              m: 0,
              my: 0.5,
              pl: 1.25,
              borderLeft: `2px solid ${t.tokens.border.strong}`,
              color: t.tokens.text.secondary,
            })}
          >
            {g.lines.join('\n').trim()}
          </Box>
        ) : (
          <React.Fragment key={i}>{g.lines.join('\n').trim()}</React.Fragment>
        ),
      )}
    </>
  );
}

/**
 * One turn in a conversation.
 *
 * Replies run the full width and your own messages are light bubbles, which
 * keeps a long conversation easy to scan. A long message folds after a few
 * lines with "Show more". Pass a reply's pieces as children — steps, results,
 * text, actions — and they stack with even spacing.
 *
 * User text is shown exactly as typed; render model output with `Markdown`.
 */
export function ChatMessage({ role, children, above }: ChatMessageProps) {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const text = typeof children === 'string' ? children : null;

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || role !== 'user') return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [role, text]);

  if (role === 'user') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, mt: 1 }}>
        {above && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.5, maxWidth: '75%' }}>
            {above}
          </Box>
        )}
        <Box
          sx={(t) => ({
            maxWidth: '75%',
            px: 1.5,
            py: 1,
            bgcolor: t.tokens.brand.primarySofter,
            borderRadius: `${t.ds.radius.lg}px ${t.ds.radius.lg}px ${t.ds.radius.xs}px ${t.ds.radius.lg}px`,
          })}
        >
          <Typography
            ref={bodyRef}
            component="div"
            variant="body2"
            sx={(t) => ({
              color: t.tokens.text.primary,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              overflow: 'hidden',
              maxHeight: expanded ? 'none' : `${FOLDED_LINES * 1.5}em`,
            })}
          >
            {text !== null ? <UserText text={text} /> : children}
          </Typography>
          {(overflows || expanded) && (
            <ButtonBase
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              sx={(t) => ({
                ...t.typography.caption,
                mt: 0.5,
                color: t.tokens.text.accent,
                fontWeight: 500,
                borderRadius: `${t.ds.radius.xs}px`,
                '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
              })}
            >
              {expanded ? 'Show less' : 'Show more'}
            </ButtonBase>
          )}
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, width: '100%', minWidth: 0 }}>
      {children}
    </Box>
  );
}
